import type { DeviceMetric } from '../../../../services/deviceMetricTypes';
import type { MetricInput as MetricPresentationInput, DailyAggregation } from '../../collection/metrics/types';
import { metricCategories, MetricCategory } from '../../collection/metrics/categories';
import { humanize } from '../../collection/metrics/formatting';

interface MetricDefinition {
  category: MetricCategory;
  label: string;
  description: string;
  verification: 'verified' | 'unverified';
}

// Presentation only: the API catalog remains the authority for routes and fields.
// Verification is an explicit review result, independent of participant availability.
const definitions: Record<string, MetricDefinition> = {
  steps: {
    category: 'activity',
    label: 'Steps',
    description: 'Steps recorded throughout the day.',
    verification: 'verified',
  },
  distance: {
    category: 'activity',
    label: 'Distance',
    description: 'Distance covered during recorded activity.',
    verification: 'verified',
  },
  'total-calories': {
    category: 'activity',
    label: 'Total calories burned',
    description: 'Daily energy expenditure, including rest.',
    verification: 'verified',
  },
  'active-energy-burned': {
    category: 'activity',
    label: 'Active calories',
    description: 'Energy burned during activity.',
    verification: 'unverified',
  },
  'active-minutes': {
    category: 'activity',
    label: 'Active minutes',
    description: 'Time by light, moderate and vigorous activity.',
    verification: 'verified',
  },
  'active-zone-minutes': {
    category: 'activity',
    label: 'Active Zone Minutes',
    description: 'Fitbit activity credits by heart-rate zone.',
    verification: 'verified',
  },
  'activity-level': {
    category: 'activity',
    label: 'Activity intensity',
    description: 'Recorded periods at each activity level.',
    verification: 'verified',
  },
  altitude: {
    category: 'activity',
    label: 'Elevation gain',
    description: 'Height gained during recorded activity.',
    verification: 'unverified',
  },
  floors: {
    category: 'activity',
    label: 'Floors climbed',
    description: 'Daily count of floors climbed.',
    verification: 'unverified',
  },
  exercise: {
    category: 'workouts',
    label: 'Workouts',
    description: 'Duration, calories, distance and heart rate for recorded workouts.',
    verification: 'verified',
  },
  'daily-vo2-max': {
    category: 'activity',
    label: 'Daily VO₂ max',
    description: 'Daily estimate of aerobic fitness.',
    verification: 'unverified',
  },
  'run-vo2-max': {
    category: 'activity',
    label: 'Running VO₂ max',
    description: 'Aerobic fitness estimates from runs.',
    verification: 'unverified',
  },
  'vo2-max': {
    category: 'activity',
    label: 'VO₂ max readings',
    description: 'Individual aerobic fitness estimates.',
    verification: 'unverified',
  },
  'sedentary-period': {
    category: 'activity',
    label: 'Sedentary periods',
    description: 'Recorded intervals of inactivity.',
    verification: 'verified',
  },
  'heart-rate': {
    category: 'heart',
    label: 'Heart rate',
    description: 'Heart-rate readings throughout the day.',
    verification: 'verified',
  },
  'daily-resting-heart-rate': {
    category: 'heart',
    label: 'Resting heart rate',
    description: 'Daily resting heart-rate measurements.',
    verification: 'verified',
  },
  'daily-heart-rate-variability': {
    category: 'heart',
    label: 'Daily HRV',
    description: 'Daily and deep-sleep RMSSD, plus non-REM heart rate.',
    verification: 'verified',
  },
  'heart-rate-variability': {
    category: 'heart',
    label: 'HRV readings',
    description: 'Individual heart-rate variability measurements.',
    verification: 'verified',
  },
  'daily-heart-rate-zones': {
    category: 'heart',
    label: 'Heart-rate zone limits',
    description: 'BPM boundaries defining each zone, by date.',
    verification: 'verified',
  },
  'time-in-heart-rate-zone': {
    category: 'heart',
    label: 'Time in heart-rate zones',
    description: 'Recorded intervals spent in each zone.',
    verification: 'verified',
  },
  'calories-in-heart-rate-zone': {
    category: 'heart',
    label: 'Calories by heart-rate zone',
    description: 'Daily energy expenditure split by zone.',
    verification: 'verified',
  },
  'daily-oxygen-saturation': {
    category: 'heart',
    label: 'Daily blood oxygen',
    description: 'Daily oxygen saturation summary (SpO₂).',
    verification: 'verified',
  },
  'oxygen-saturation': {
    category: 'heart',
    label: 'Blood oxygen readings',
    description: 'Individual oxygen saturation measurements (SpO₂).',
    verification: 'verified',
  },
  'daily-respiratory-rate': {
    category: 'heart',
    label: 'Daily breathing rate',
    description: 'Average breathing rate during the main sleep, once per day.',
    verification: 'verified',
  },
  'irregular-rhythm-notification': {
    category: 'heart',
    label: 'Irregular rhythm notifications',
    description: 'Notification periods reported by Fitbit.',
    verification: 'unverified',
  },
  sleep: {
    category: 'sleep',
    label: 'Sleep duration',
    description: 'Time asleep, awake and falling asleep.',
    verification: 'verified',
  },
  'respiratory-rate-sleep-summary': {
    category: 'sleep',
    label: 'Breathing during sleep',
    description: 'Breathing rates by sleep stage.',
    verification: 'verified',
  },
  'daily-sleep-temperature-derivations': {
    category: 'sleep',
    label: 'Sleep skin temperature',
    description: 'Nightly skin temperature, 30-day baseline and variation.',
    verification: 'verified',
  },
  weight: {
    category: 'body',
    label: 'Weight',
    description: 'Recorded body weight.',
    verification: 'unverified',
  },
  height: {
    category: 'body',
    label: 'Height',
    description: 'Recorded height measurements.',
    verification: 'unverified',
  },
  'body-fat': {
    category: 'body',
    label: 'Body fat',
    description: 'Recorded body-fat percentage.',
    verification: 'unverified',
  },
  'core-body-temperature': {
    category: 'body',
    label: 'Core body temperature',
    description: 'Core body temperature measurements in Celsius.',
    verification: 'unverified',
  },
  'blood-glucose': {
    category: 'body',
    label: 'Blood glucose',
    description: 'Recorded glucose measurements.',
    verification: 'unverified',
  },
  'hydration-log': {
    category: 'nutrition',
    label: 'Water intake',
    description: 'Logged amounts of water consumed.',
    verification: 'unverified',
  },
  'nutrition-log': {
    category: 'nutrition',
    label: 'Food intake',
    description: 'Logged calories, carbohydrate and fat.',
    verification: 'unverified',
  },
};

export function metricPresentation(metric: MetricPresentationInput) {
  const definition = definitions[metric.dataType];
  const declaredCategory = (Object.keys(metricCategories) as MetricCategory[]).find(key => metricCategories[key].label === metric.category);
  const category = declaredCategory || definition?.category || 'other';
  const palette = metricCategories[category];
  return {
    category,
    label: metric.label || definition?.label || humanize(metric.dataType),
    description: metric.description || definition?.description || 'Explore the recorded measurements.',
    color: palette.color,
    background: palette.background,
    selectedBackground: palette.selectedBackground,
    border: palette.border,
    unverified: (metric.verification || definition?.verification) === 'unverified',
    verified: (metric.verification || definition?.verification) === 'verified',
  };
}

export function recordingKind(metric: DeviceMetric) {
  if (metric.dataType === 'exercise') return 'Workout sessions';
  if (metric.dataType === 'sleep') return 'Sleep sessions';
  if (metric.operation === 'daily_rollup' || metric.temporalKind === 'daily') return 'Daily summaries';
  return ({ point: 'Individual readings', sample: 'Individual readings', interval: 'Recorded intervals', session: 'Sessions' } as Record<string, string>)[metric.temporalKind] || 'Records';
}

// These are related views, never substitutes or inputs to a combined total.
// Google Health documents these five workout-summary / telemetry relationships:
// https://developers.google.com/health/data-types/workouts#related_telemetry_data_types
export const workoutRelatedMeasurements: Record<string, { dataType: string; description: string }> = {
  '$.metricsSummary.caloriesKcal': { dataType: 'active-energy-burned', description: 'Calories reported for each workout.' },
  '$.metricsSummary.distanceMillimeters': { dataType: 'distance', description: 'Distance reported for each workout.' },
  '$.metricsSummary.steps': { dataType: 'steps', description: 'Steps reported for each workout.' },
  '$.metricsSummary.averageHeartRateBeatsPerMinute': { dataType: 'heart-rate', description: 'The average heart rate reported for each workout.' },
  '$.metricsSummary.activeZoneMinutes': { dataType: 'active-zone-minutes', description: 'Activity credits earned during each workout.' },
};

const fieldLabels: Record<string, string> = {
  beatsPerMinute: 'Heart rate', count: 'Steps', countSum: 'Floors climbed', kcal: 'Energy burned', kcalSum: 'Calories burned',
  millimeters: 'Distance', gainMillimeters: 'Elevation gain', bloodGlucoseMilligramsPerDeciliter: 'Blood glucose',
  percentage: 'Percentage', temperatureCelsius: 'Temperature', averageHeartRateVariabilityMilliseconds: 'Average HRV (RMSSD)',
  deepSleepRootMeanSquareOfSuccessiveDifferencesMilliseconds: 'Deep-sleep HRV (RMSSD)', nonRemHeartRateBeatsPerMinute: 'Non-REM heart rate',
  averagePercentage: 'Average oxygen saturation', lowerBoundPercentage: 'Lower SpO₂ confidence bound',
  upperBoundPercentage: 'Upper SpO₂ confidence bound', standardDeviationPercentage: 'Oxygen saturation variation (SD)',
  breathsPerMinute: 'Breathing rate', baselineTemperatureCelsius: 'Baseline temperature', nightlyTemperatureCelsius: 'Nightly temperature',
  relativeNightlyStddev30dCelsius: 'Relative nightly variation (30 days)', vo2Max: 'VO₂ max', runVo2Max: 'Running VO₂ max',
  activeDuration: 'Active workout time', activeZoneMinutes: 'Active Zone Minutes', averageHeartRateBeatsPerMinute: 'Average heart rate',
  averagePaceSecondsPerMeter: 'Average pace', averageSpeedMillimetersPerSecond: 'Average speed', caloriesKcal: 'Calories burned',
  distanceMillimeters: 'Distance', elevationGainMillimeters: 'Elevation gain', lightTime: 'Time in light zone',
  moderateTime: 'Time in moderate zone', vigorousTime: 'Time in vigorous zone', peakTime: 'Time in peak zone',
  avgCadenceStepsPerMinute: 'Average cadence', avgGroundContactTimeDuration: 'Ground contact time', avgStrideLengthMillimeters: 'Stride length',
  avgVerticalOscillationMillimeters: 'Vertical oscillation', avgVerticalRatio: 'Vertical ratio', steps: 'Steps',
  rootMeanSquareOfSuccessiveDifferencesMilliseconds: 'HRV (RMSSD)', standardDeviationMilliseconds: 'HRV (SDNN)',
  heightMillimeters: 'Height', milliliters: 'Water consumed', standardDeviation: 'Breathing rate variation (SD)',
  minutesAfterWakeUp: 'Time after waking', minutesAsleep: 'Time asleep', minutesAwake: 'Time awake',
  minutesInSleepPeriod: 'Sleep period (all stages)', minutesToFallAsleep: 'Time to fall asleep', weightGrams: 'Weight',
};
const exactFieldLabels: Record<string, string> = {
  '$.energy.kcal': 'Food energy', '$.energyFromFat.kcal': 'Energy from fat',
  '$.totalCarbohydrate.grams': 'Carbohydrate', '$.totalFat.grams': 'Fat',
};
const additiveFields = new Set(['count', 'countSum', 'kcal', 'kcalSum', 'millimeters', 'gainMillimeters', 'activeDuration',
  'activeZoneMinutes', 'caloriesKcal', 'distanceMillimeters', 'elevationGainMillimeters', 'lightTime', 'moderateTime',
  'vigorousTime', 'peakTime', 'steps', 'milliliters', 'grams', 'minutesAfterWakeUp', 'minutesAsleep', 'minutesAwake',
  'minutesInSleepPeriod', 'minutesToFallAsleep']);

export function recordKindLabel(dataType: string) {
  if (dataType === 'daily-heart-rate-zones') return 'Zone definitions';
  if (dataType === 'total-calories') return 'Estimated calories';
  return 'Stored record';
}

export function fieldPresentation(metric: DeviceMetric, field: DeviceMetric['fields'][number]) {
  const key = field.path.split('.').pop() || field.path;
  let label = exactFieldLabels[field.path] || fieldLabels[key] || humanize(field.path);
  const stage = ({ deepSleepStats: 'Deep sleep', fullSleepStats: 'Full sleep', lightSleepStats: 'Light sleep', remSleepStats: 'REM sleep' } as Record<string, string>)[field.path.split('.')[1]];
  if (stage) label = `${stage} · ${label.toLowerCase()}`;
  if (key === 'percentage') label = metric.dataType === 'body-fat' ? 'Body fat' : 'Oxygen saturation';
  if (key === 'beatsPerMinute' && metric.dataType === 'daily-resting-heart-rate') label = 'Resting heart rate';
  let unit = field.unit === 'Cel' ? '°C' : field.unit || '';
  if (key === 'activeZoneMinutes') unit = 'credits';
  let divisor = 1;
  if (metric.dataType === 'weight' && unit === 'g') { unit = 'kg'; divisor = 1000; }
  if (metric.dataType === 'height' && unit === 'mm') { unit = 'cm'; divisor = 10; }
  if (unit === 'mm' && (metric.dataType === 'distance' || key === 'distanceMillimeters')) { unit = 'km'; divisor = 1000000; }
  if (unit === 'mm' && /gain/i.test(key)) { unit = 'm'; divisor = 1000; }
  if (unit === 'count') unit = metric.dataType === 'floors' ? 'floors' : key === 'steps' || metric.dataType === 'steps' ? 'steps' : 'count';
  const additive = additiveFields.has(key);
  return { label, unit, divisor, additive, defaultAggregation: (additive ? 'total' : 'average') as DailyAggregation };
}

export function defaultField(metric: DeviceMetric | undefined) {
  const preferred = ({ sleep: '$.summary.minutesAsleep', exercise: '$.activeDuration', 'nutrition-log': '$.energy.kcal',
    'respiratory-rate-sleep-summary': '$.fullSleepStats.breathsPerMinute',
    'daily-sleep-temperature-derivations': '$.nightlyTemperatureCelsius' } as Record<string, string>)[metric?.dataType || ''];
  return metric?.fields.find(field => field.path === preferred)?.path || metric?.fields[0]?.path || '';
}
